/**
 * Crisis Detector Service
 *
 * Assesses student risk levels based on multiple factors to determine
 * when API intervention is needed. Focuses on observable patterns
 * rather than psychological diagnosis.
 */

import { differenceInDays, differenceInHours, parseISO, isAfter, isBefore } from 'date-fns';

export class CrisisDetector {
  constructor() {
    // Thresholds for crisis detection
    this.thresholds = {
      critical: {
        overdueCount: 15,
        criticalOverdueCount: 5,
        hoursOverdue: 100,
        completionRate: 0.2,
        deadlineProximityHours: 24
      },
      crisis: {
        overdueCount: 10,
        criticalOverdueCount: 3,
        hoursOverdue: 50,
        completionRate: 0.3,
        deadlineProximityHours: 48
      },
      highStress: {
        overdueCount: 5,
        criticalOverdueCount: 2,
        hoursOverdue: 25,
        completionRate: 0.5,
        deadlineProximityHours: 72
      },
      moderate: {
        overdueCount: 3,
        criticalOverdueCount: 1,
        hoursOverdue: 10,
        completionRate: 0.7,
        deadlineProximityHours: 168 // 1 week
      }
    };

    // Weights for different factors
    this.factorWeights = {
      overdueCount: 0.15,
      criticalOverdue: 0.25,
      hoursOverdue: 0.10,
      next48HourTasks: 0.15,
      nextWeekHours: 0.10,
      deadlineCluster: 0.05,
      completionTrend: 0.08,
      reschedulingFrequency: 0.06,
      sessionFragmentation: 0.03,
      lastActiveHours: 0.01,
      abandonmentRate: 0.01,
      helpRequestFrequency: 0.01
    };
  }

  /**
   * Main assessment method
   * @param {String} userId - User ID for historical data
   * @param {Array} timelineData - Current timeline tasks
   * @returns {Object} Crisis assessment with level, score, and recommendations
   */
  async assessCrisisLevel(userId, timelineData) {
    try {
      const factors = await this.calculateRiskFactors(userId, timelineData);
      const crisisScore = this.calculateWeightedScore(factors);
      const level = this.determineLevel(crisisScore, factors);
      const triggers = this.identifyTriggers(factors);

      return {
        level,
        score: crisisScore,
        factors,
        triggers,
        confidence: this.calculateConfidence(factors),
        recommendedAction: this.getRecommendedAction(level, factors),
        reasoning: this.explainAssessment(level, factors, triggers)
      };
    } catch (error) {
      console.error('[CrisisDetector] Assessment failed:', error);
      // Conservative fallback
      return {
        level: 'moderate',
        score: 0.5,
        confidence: 0.5,
        error: error.message,
        recommendedAction: 'local_optimization'
      };
    }
  }

  /**
   * Calculate all risk factors
   */
  async calculateRiskFactors(userId, timelineData) {
    const now = new Date();
    const activeTasks = timelineData.filter(t => !t.completed);

    return {
      // Immediate pressure (40% of total weight)
      overdueCount: this.countOverdueTasks(activeTasks, now),
      criticalOverdue: this.countCriticalOverdue(activeTasks, now),
      hoursOverdue: this.calculateOverdueHours(activeTasks, now),

      // Upcoming pressure (30% of total weight)
      next48HourTasks: this.getTasksInWindow(activeTasks, now, 48),
      nextWeekHours: this.calculateHoursInWindow(activeTasks, now, 168),
      deadlineCluster: this.detectDeadlineClustering(activeTasks),

      // Behavioral patterns (20% of total weight)
      completionTrend: await this.analyzeCompletionTrend(timelineData, 14),
      reschedulingFrequency: this.analyzeReschedulingPattern(activeTasks),
      sessionFragmentation: await this.analyzeSessionQuality(userId),

      // System usage patterns (10% of total weight)
      lastActiveHours: await this.hoursSinceLastActivity(userId),
      abandonmentRate: this.calculateAbandonmentRate(timelineData, 7),
      helpRequestFrequency: await this.getHelpRequestCount(userId, 7)
    };
  }

  /**
   * Count overdue tasks
   */
  countOverdueTasks(tasks, now) {
    return tasks.filter(task => {
      if (!task.deadline && !task.scheduled_date) return false;
      const dueDate = new Date(task.deadline || task.scheduled_date);
      return isBefore(dueDate, now);
    }).length;
  }

  /**
   * Count critical overdue tasks
   */
  countCriticalOverdue(tasks, now) {
    return tasks.filter(task => {
      if (!task.deadline && !task.scheduled_date) return false;
      const dueDate = new Date(task.deadline || task.scheduled_date);
      return isBefore(dueDate, now) &&
             (task.priority === 'critical' || task.priority === 'high');
    }).length;
  }

  /**
   * Calculate total hours of overdue work
   */
  calculateOverdueHours(tasks, now) {
    const overdueTasks = tasks.filter(task => {
      if (!task.deadline && !task.scheduled_date) return false;
      const dueDate = new Date(task.deadline || task.scheduled_date);
      return isBefore(dueDate, now);
    });

    return overdueTasks.reduce((sum, task) => {
      return sum + (task.estimated_hours || 0.75);
    }, 0);
  }

  /**
   * Get tasks within time window
   */
  getTasksInWindow(tasks, now, hours) {
    const windowEnd = new Date(now.getTime() + hours * 60 * 60 * 1000);

    return tasks.filter(task => {
      if (!task.deadline && !task.scheduled_date) return false;
      const dueDate = new Date(task.deadline || task.scheduled_date);
      return isAfter(dueDate, now) && isBefore(dueDate, windowEnd);
    }).length;
  }

  /**
   * Calculate hours of work in time window
   */
  calculateHoursInWindow(tasks, now, hours) {
    const windowEnd = new Date(now.getTime() + hours * 60 * 60 * 1000);

    const tasksInWindow = tasks.filter(task => {
      if (!task.deadline && !task.scheduled_date) return false;
      const dueDate = new Date(task.deadline || task.scheduled_date);
      return isAfter(dueDate, now) && isBefore(dueDate, windowEnd);
    });

    return tasksInWindow.reduce((sum, task) => {
      return sum + (task.estimated_hours || 0.75);
    }, 0);
  }

  /**
   * Detect deadline clustering
   */
  detectDeadlineClustering(tasks) {
    const deadlineGroups = {};

    tasks.forEach(task => {
      if (!task.deadline && !task.scheduled_date) return;

      const date = new Date(task.deadline || task.scheduled_date);
      const weekKey = `${date.getFullYear()}-W${this.getWeekNumber(date)}`;

      if (!deadlineGroups[weekKey]) {
        deadlineGroups[weekKey] = [];
      }
      deadlineGroups[weekKey].push(task);
    });

    // Find weeks with high concentration
    const clusteredWeeks = Object.values(deadlineGroups).filter(
      group => group.length >= 5
    );

    // Return severity score (0-1)
    if (clusteredWeeks.length === 0) return 0;

    const maxCluster = Math.max(...clusteredWeeks.map(g => g.length));
    return Math.min(1, maxCluster / 10); // Normalize to 0-1
  }

  /**
   * Analyze completion trend over time
   */
  async analyzeCompletionTrend(timelineData, days) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    const recentTasks = timelineData.filter(task => {
      if (!task.updated_at) return false;
      const updateDate = new Date(task.updated_at);
      return isAfter(updateDate, cutoffDate);
    });

    if (recentTasks.length === 0) return 1.0; // No recent data, assume okay

    const completedCount = recentTasks.filter(t => t.completed).length;
    const completionRate = completedCount / recentTasks.length;

    // Invert for risk score (lower completion = higher risk)
    return 1 - completionRate;
  }

  /**
   * Analyze task rescheduling patterns
   */
  analyzeReschedulingPattern(tasks) {
    const churningTasks = tasks.filter(task =>
      task.reschedule_count && task.reschedule_count >= 3
    );

    // Normalize to 0-1 (3+ reschedules for 20% of tasks = max risk)
    const churnRate = churningTasks.length / Math.max(1, tasks.length);
    return Math.min(1, churnRate * 5);
  }

  /**
   * Analyze session quality (placeholder for actual implementation)
   */
  async analyzeSessionQuality(userId) {
    // This would analyze actual session data
    // For now, return neutral value
    return 0.5;
  }

  /**
   * Hours since last activity (placeholder)
   */
  async hoursSinceLastActivity(userId) {
    // This would check actual activity logs
    // For now, return low risk value
    return 0.1;
  }

  /**
   * Calculate task abandonment rate
   */
  calculateAbandonmentRate(timelineData, days) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    const recentTasks = timelineData.filter(task => {
      if (!task.created_at) return false;
      const createDate = new Date(task.created_at);
      return isAfter(createDate, cutoffDate);
    });

    if (recentTasks.length === 0) return 0;

    // Tasks that were deleted or marked as "won't do"
    const abandonedCount = recentTasks.filter(t =>
      t.deleted || t.status === 'abandoned' || t.priority === 'wont_do'
    ).length;

    return abandonedCount / recentTasks.length;
  }

  /**
   * Get help request frequency (placeholder)
   */
  async getHelpRequestCount(userId, days) {
    // This would check actual help request logs
    return 0;
  }

  /**
   * Calculate weighted crisis score
   */
  calculateWeightedScore(factors) {
    let score = 0;

    for (const [factor, value] of Object.entries(factors)) {
      const weight = this.factorWeights[factor] || 0;
      const normalized = this.normalizeFactor(factor, value);
      score += normalized * weight;
    }

    return Math.min(1.0, Math.max(0, score));
  }

  /**
   * Normalize factor values to 0-1 scale
   */
  normalizeFactor(factor, value) {
    const normalizers = {
      overdueCount: (v) => Math.min(1, v / 20),
      criticalOverdue: (v) => Math.min(1, v / 5),
      hoursOverdue: (v) => Math.min(1, v / 100),
      next48HourTasks: (v) => Math.min(1, v / 10),
      nextWeekHours: (v) => Math.min(1, v / 40),
      deadlineCluster: (v) => v, // Already 0-1
      completionTrend: (v) => v, // Already 0-1
      reschedulingFrequency: (v) => v, // Already 0-1
      sessionFragmentation: (v) => v, // Already 0-1
      lastActiveHours: (v) => Math.min(1, v / 168), // Cap at 1 week
      abandonmentRate: (v) => v, // Already 0-1
      helpRequestFrequency: (v) => Math.min(1, v / 10)
    };

    const normalizer = normalizers[factor];
    return normalizer ? normalizer(value) : value;
  }

  /**
   * Determine crisis level from score and factors
   */
  determineLevel(score, factors) {
    // Override based on critical factors
    if (factors.criticalOverdue >= this.thresholds.critical.criticalOverdueCount ||
        factors.overdueCount >= this.thresholds.critical.overdueCount) {
      return 'critical';
    }

    if (factors.criticalOverdue >= this.thresholds.crisis.criticalOverdueCount ||
        factors.overdueCount >= this.thresholds.crisis.overdueCount) {
      return 'crisis';
    }

    // Score-based determination
    if (score >= 0.8) return 'critical';
    if (score >= 0.6) return 'crisis';
    if (score >= 0.4) return 'high_stress';
    if (score >= 0.2) return 'moderate';
    return 'normal';
  }

  /**
   * Identify specific triggers for the crisis level
   */
  identifyTriggers(factors) {
    const triggers = [];

    if (factors.overdueCount > 10) {
      triggers.push({
        type: 'overdue_overload',
        severity: 'high',
        message: `${factors.overdueCount} overdue tasks creating backlog`
      });
    }

    if (factors.criticalOverdue > 2) {
      triggers.push({
        type: 'critical_deadline_risk',
        severity: 'critical',
        message: `${factors.criticalOverdue} critical tasks are overdue`
      });
    }

    if (factors.next48HourTasks > 5) {
      triggers.push({
        type: 'immediate_overload',
        severity: 'high',
        message: `${factors.next48HourTasks} tasks due in next 48 hours`
      });
    }

    if (factors.completionTrend > 0.7) {
      triggers.push({
        type: 'declining_completion',
        severity: 'medium',
        message: 'Completion rate declining significantly'
      });
    }

    if (factors.reschedulingFrequency > 0.6) {
      triggers.push({
        type: 'schedule_instability',
        severity: 'medium',
        message: 'Tasks being repeatedly rescheduled'
      });
    }

    return triggers;
  }

  /**
   * Calculate confidence in assessment
   */
  calculateConfidence(factors) {
    // Higher confidence with more data points
    let dataPoints = 0;
    let totalPossible = Object.keys(factors).length;

    Object.values(factors).forEach(value => {
      if (value !== null && value !== undefined && value !== 0) {
        dataPoints++;
      }
    });

    return dataPoints / totalPossible;
  }

  /**
   * Get recommended action based on level and factors
   */
  getRecommendedAction(level, factors) {
    switch (level) {
      case 'critical':
        return {
          action: 'immediate_api_intervention',
          priority: 'urgent',
          description: 'Generate comprehensive recovery plan immediately'
        };

      case 'crisis':
        return {
          action: 'api_recovery_plan',
          priority: 'high',
          description: 'Create structured recovery plan to get back on track'
        };

      case 'high_stress':
        return {
          action: 'api_optimization',
          priority: 'medium',
          description: 'Optimize schedule to reduce stress'
        };

      case 'moderate':
        return {
          action: 'local_suggestions',
          priority: 'low',
          description: 'Provide local optimization suggestions'
        };

      default:
        return {
          action: 'monitor',
          priority: 'none',
          description: 'Continue monitoring, no intervention needed'
        };
    }
  }

  /**
   * Explain the assessment in human terms
   */
  explainAssessment(level, factors, triggers) {
    const explanations = {
      critical: `Critical situation detected with ${factors.overdueCount} overdue tasks and ${factors.criticalOverdue} critical deadlines at risk. Immediate comprehensive help recommended.`,
      crisis: `You're overwhelmed with ${factors.overdueCount} overdue tasks and ${factors.next48HourTasks} due soon. Let's create a recovery plan to get you back on track.`,
      high_stress: `High stress detected with ${factors.overdueCount} overdue tasks. Some schedule optimization would help reduce pressure.`,
      moderate: `Moderate stress level. A few adjustments could improve your workflow.`,
      normal: `Everything looks manageable. Keep up the good work!`
    };

    return {
      summary: explanations[level] || explanations.normal,
      triggers: triggers.map(t => t.message),
      confidence: `Assessment confidence: ${Math.round(this.calculateConfidence(factors) * 100)}%`
    };
  }

  /**
   * Helper: Get week number
   */
  getWeekNumber(date) {
    const firstJan = new Date(date.getFullYear(), 0, 1);
    const days = Math.floor((date - firstJan) / (24 * 60 * 60 * 1000));
    return Math.ceil((days + firstJan.getDay() + 1) / 7);
  }
}